/**
 * 旧版「明细表格」→ 新版「表格」的升级。
 *
 * 两种表格块并存是历史包袱：明细表格能带子表多行却不能合并单元格，自由表格
 * 能合并却只能填主记录字段。用户得先想清楚该用哪一种，而竞品只有一种表格、
 * 用下拉切数据源。新版「表格」已经是明细表格的超集（动态行 + 合并 + 合计行 +
 * 斑马纹 + 共 N 条），所以旧的可以整体搬过去。
 *
 * 转换结果：第 1 行是表头（列名，加粗带底纹），第 2 行是样板行（绑字段），
 * 序号列、合计行、斑马纹按原配置带过去。排序和筛选**不带**——它们是明细表格
 * 特有的，新表格没有对应能力，转换时会在返回值里说明，不静默丢掉。
 */
import { INDEX_REF } from './renderer';
import { genId } from './types';
import type {
  DetailTableSection,
  FreeTableCell,
  FreeTableSection,
} from './types';

function cell(content: string, extra: Partial<FreeTableCell> = {}): FreeTableCell {
  return {
    id: genId(),
    content,
    colSpan: 1,
    rowSpan: 1,
    align: 'left',
    bold: false,
    shaded: false,
    merged: false,
    ...extra,
  };
}

export interface TableMigration {
  section: FreeTableSection;
  /** 转换中丢掉的能力，界面要如实告诉用户。 */
  lost: string[];
}

export function migrateDetailTable(s: DetailTableSection): TableMigration {
  const lost: string[] = [];
  if (s.sort?.columnId) lost.push('明细排序');
  if (s.filter?.columnId) lost.push('明细筛选');

  // 序号列：新表格用 INDEX_REF 这个特殊绑定，展开时自动 1、2、3…
  const cols: Array<{ label: string; fieldId: string; align?: FreeTableCell['align'] }> = [
    ...(s.showIndex ? [{ label: '#', fieldId: INDEX_REF, align: 'center' as const }] : []),
    ...s.columns.map((c) => ({ label: c.label, fieldId: c.fieldId, align: c.align })),
  ];

  const n = Math.max(1, cols.length);
  const width = Math.round((100 / n) * 10) / 10;
  const colWidths = cols.map(() => width);
  // 浮点凑不满 100 时把差额补给最后一列
  if (colWidths.length) {
    colWidths[colWidths.length - 1] = Math.round((100 - width * (n - 1)) * 10) / 10;
  }

  const columnFields: Record<number, string> = {};
  cols.forEach((c, i) => {
    if (c.fieldId) columnFields[i] = c.fieldId;
  });

  const summaryCols: number[] = [];
  if (s.summaryRow) {
    const byId = new Map(s.columns.map((c, i) => [c.id, i + (s.showIndex ? 1 : 0)]));
    for (const id of s.summaryRow.sumColumns) {
      const at = byId.get(id);
      if (at !== undefined) summaryCols.push(at);
    }
  }

  return {
    section: {
      type: 'free_table',
      id: s.id,
      colWidths,
      borderPx: 1,
      fontSizePx: null,
      rows: [
        {
          id: genId(),
          heightMm: null,
          cells: cols.map((c) =>
            cell(c.label ?? '', { bold: true, shaded: true, align: 'center' }),
          ),
        },
        {
          id: genId(),
          heightMm: null,
          cells: cols.map((c) => cell('', { align: c.align ?? 'left' })),
        },
      ],
      repeat: {
        source: 'link',
        linkFieldId: s.linkFieldId,
        rowIndex: 1,
        columnFields,
      },
      summary: s.summaryRow ? { label: s.summaryRow.label || '合计', columns: summaryCols } : null,
    },
    lost,
  };
}
